import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { StudioLayout } from "@/components/StudioLayout";
import { useBrandAccent } from "@/context/BrandContext";
import { PostPreview } from "@/components/PostPreview";
import { FileUpload } from "@/components/FileUpload";
import { VideoPlaceholder } from "@/components/MediaThumb";
import { Comments } from "@/components/Comments";
import { CHANNELS, POST_TYPES, STATUSES, channelMeta, statusMeta } from "@/lib/constants";
import { StatusDot } from "@/components/StatusDot";
import { ArrowLeft, Send, History, Trash2, Archive, GripVertical, X, Camera, MessageSquareWarning } from "lucide-react";
import { toast } from "sonner";
import { fmtSeconds, fmtTs, todayStr } from "@/lib/format";

export default function PostDetail() {
  const { brandId, postId } = useParams();
  const navigate = useNavigate();
  const { setAccent } = useBrandAccent();
  const [brand, setBrand] = useState(null);
  const [post, setPost] = useState(null);
  const [tab, setTab] = useState("form");
  const [videoSecond, setVideoSecond] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [savedState, setSavedState] = useState("idle");
  const skipAutosave = useRef(true);
  const autoCoverRef = useRef(false);
  const videoElRef = useRef(null);
  const dragIdxRef = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  useEffect(() => {
    api.get(`/brands/${brandId}`).then((r) => { setBrand(r.data); setAccent(r.data.color); });
    api.get(`/posts/${postId}`).then((r) => setPost(r.data));
  }, [postId]);

  const refresh = () => api.get(`/posts/${postId}`).then((r) => setPost(r.data));

  useEffect(() => {
    if (!post) return;
    if (skipAutosave.current) { skipAutosave.current = false; return; }
    setSavedState("saving");
    const t = setTimeout(async () => {
      try {
        await api.put(`/posts/${postId}`, { title: post.title, date: post.date, time: post.time, channel: post.channel, type: post.type, text: post.text, hashtags: post.hashtags, status: post.status, cover_url: post.cover_url });
        setSavedState("saved");
      } catch { setSavedState("idle"); }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [post?.date, post?.time, post?.channel, post?.type, post?.text, post?.hashtags, post?.cover_url]);

  useEffect(() => {
    if (!post || autoCoverRef.current) return;
    const vid = post.files?.find((f) => f.type === "video");
    if (vid && !post.cover_url) {
      autoCoverRef.current = true;
      coverFromUrl(vid.url).then((c) => setCover({ url: c }, true)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [post]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/posts/${postId}`, {
        title: post.title, date: post.date, time: post.time, channel: post.channel,
        type: post.type, text: post.text, hashtags: post.hashtags, status: post.status, cover_url: post.cover_url,
      });
      setPost({ ...data });
      toast.success("Guardado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const setStatus = async (s) => { const { data } = await api.patch(`/posts/${postId}/status`, { status: s }); setPost(data); };
  const send = async () => { const { data } = await api.post(`/posts/${postId}/send`); setPost(data); toast.success("Enviado al cliente"); };
  const archive = async () => { const { data } = await api.post(`/posts/${postId}/archive`); setPost(data); toast.success(data.archived ? "Archivado" : "Restaurado"); };
  const newVersion = async () => { const { data } = await api.post(`/posts/${postId}/new-version`); setPost(data); toast.success(`Nueva versión v${data.version}. El post vuelve a enviado al cliente.`); };
  const del = async () => { await api.delete(`/posts/${postId}`); toast.success("Post eliminado"); navigate(`/brand/${brandId}/calendar`); };

  const attach = async (uploaded) => {
    await api.post(`/posts/${postId}/files`, { url: uploaded.url, is_video: uploaded.is_video, thumbnail: uploaded.url });
    if (uploaded.is_video && !post.cover_url) {
      try { const c = await coverFromUrl(uploaded.url); await setCover({ url: c }, true); } catch (e) {}
    }
    refresh();
  };
  const removeFile = async (fid) => { await api.delete(`/files/item/${fid}`); refresh(); };

  const reorderFiles = async (from, to) => {
    if (from === to || from == null || to == null) return;
    const files = [...(post.files || [])];
    const [moved] = files.splice(from, 1);
    files.splice(to, 0, moved);
    setPost({ ...post, files });
    try { await api.patch(`/posts/${postId}/files/reorder`, { order: files.map((f) => f.id) }); }
    catch (e) { refresh(); }
  };

  const setCover = async (u, silent) => {
    const updated = { ...post, cover_url: u.url };
    setPost(updated);
    await api.put(`/posts/${postId}`, { title: updated.title, date: updated.date, time: updated.time, channel: updated.channel, type: updated.type, text: updated.text, hashtags: updated.hashtags, status: updated.status, cover_url: u.url });
    if (!silent) toast.success("Portada guardada");
  };

  const coverFromUrl = async (url) => {
    const video = document.createElement("video");
    video.src = fileUrl(url); video.crossOrigin = "anonymous"; video.muted = true; video.preload = "auto";
    await new Promise((res, rej) => { video.onloadeddata = res; video.onerror = () => rej(new Error("load")); });
    try { video.currentTime = Math.min(1, (video.duration || 2) / 2); } catch (e) {}
    await new Promise((res) => { const to = setTimeout(res, 1500); video.onseeked = () => { clearTimeout(to); res(); }; });
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 720; canvas.height = video.videoHeight || 1280;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    const fd = new FormData(); fd.append("file", new File([blob], "portada.jpg", { type: "image/jpeg" }));
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const captureFrame = async () => {
    const src = fileUrl(post.files?.find((f) => f.type === "video")?.url);
    if (!src) { toast.error("Sube primero un video."); return; }
    try {
      let video = videoElRef.current;
      if (!video || video.readyState < 2) {
        video = document.createElement("video");
        video.src = src; video.crossOrigin = "anonymous"; video.muted = true;
        await new Promise((res, rej) => { video.onloadeddata = res; video.onerror = rej; });
        video.currentTime = Math.min(1, (video.duration || 2) / 2);
        await new Promise((res) => { video.onseeked = res; });
      }
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 720;
      canvas.height = video.videoHeight || 1280;
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));
      const fd = new FormData();
      fd.append("file", new File([blob], "portada.jpg", { type: "image/jpeg" }));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await setCover({ url: data.url });
    } catch (e) {
      toast.error("No he podido tomar el fotograma. Reproduce el video un momento e inténtalo de nuevo.");
    }
  };

  const onVideoTime = (t, el) => { setVideoSecond(t); videoElRef.current = el; };
  const seek = (s) => { if (videoElRef.current) { videoElRef.current.currentTime = s; videoElRef.current.play()?.catch(() => {}); } };

  if (!post || !brand) return <StudioLayout><div className="py-20 text-center text-subtle">Cargando…</div></StudioLayout>;

  const isVideo = post.type === "video" || post.files?.some((f) => f.type === "video");
  const st = statusMeta(post.status);
  const charCount = (post.text || "").length;

  return (
    <StudioLayout brand={brand}>
      <button onClick={() => navigate(`/brand/${brandId}/calendar`)} data-testid="back-btn" className="flex items-center gap-1.5 text-sm text-subtle mb-5 hover:text-ink"><ArrowLeft size={15} /> Volver al calendario</button>

      {post.status === "cambios_solicitados" && post.change_request && (
        <div className="mb-6 border rounded-sm p-4 flex gap-3" style={{ borderColor: "#C2410C", background: "#FBEEE9" }} data-testid="change-request-banner">
          <MessageSquareWarning size={18} style={{ color: "#C2410C" }} className="shrink-0 mt-0.5" />
          <div className="text-sm min-w-0">
            <div className="font-medium" style={{ color: "#A63D2E" }}>{post.change_request.who} pidió cambios</div>
            <p className="mt-1 whitespace-pre-wrap leading-relaxed">{post.change_request.reason}</p>
            <div className="text-xs text-subtle mt-1.5 font-data">{fmtTs(post.change_request.created_at)}</div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Left: preview */}
        <div>
          <PostPreview post={post} brand={brand} onVideoTime={onVideoTime} />
          <div className="flex items-center gap-3 mt-4 justify-center">
            <span className="font-data text-xs text-subtle">v{post.version}</span>
            <span className="flex items-center gap-1.5 text-sm"><StatusDot color={st.dot} /> {st.label}</span>
            {post.version > 1 && <button onClick={() => setShowHistory(true)} data-testid="history-btn" className="text-xs text-subtle flex items-center gap-1 hover:text-ink"><History size={13} /> Historial</button>}
          </div>
        </div>

        {/* Right: tabs */}
        <div>
          <div className="flex border-b border-line mb-5">
            <Tab active={tab === "form"} onClick={() => setTab("form")} testid="tab-form">Detalles</Tab>
            <Tab active={tab === "comments"} onClick={() => setTab("comments")} testid="tab-comments">Comentarios</Tab>
          </div>

          {tab === "form" ? (
            <div className="space-y-4">
              <div>
                <span className="text-sm text-subtle">Foto o video</span>
                <p className="text-xs text-subtle mb-2">Sube primero el contenido. Puedes añadir varias imágenes para un carrusel.</p>
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {post.files?.map((f, i) => (
                    <div
                      key={f.id}
                      draggable
                      onDragStart={() => { dragIdxRef.current = i; }}
                      onDragOver={(e) => { e.preventDefault(); if (dragOver !== i) setDragOver(i); }}
                      onDragEnd={() => { dragIdxRef.current = null; setDragOver(null); }}
                      onDrop={(e) => { e.preventDefault(); reorderFiles(dragIdxRef.current, i); dragIdxRef.current = null; setDragOver(null); }}
                      data-testid={`file-thumb-${i}`}
                      className={`relative aspect-square border group cursor-move transition-shadow ${dragOver === i ? "border-ink shadow-md" : "border-line"}`}
                    >
                      {f.type === "video"
                        ? <VideoPlaceholder />
                        : <img src={fileUrl(f.thumbnail || f.url)} alt="" className="w-full h-full object-cover pointer-events-none" />}
                      {post.files.length > 1 && (
                        <>
                          <span className="absolute bottom-0.5 left-0.5 bg-ink/70 text-white text-[10px] font-data px-1 rounded-sm">{i + 1}</span>
                          <span className="absolute top-0.5 left-0.5 bg-ink/50 text-white rounded-sm p-0.5 opacity-0 group-hover:opacity-100"><GripVertical size={11} /></span>
                        </>
                      )}
                      <button onClick={() => removeFile(f.id)} data-testid={`remove-file-${i}`} className="absolute top-0.5 right-0.5 bg-ink/70 text-white rounded-sm p-0.5 opacity-0 group-hover:opacity-100"><X size={11} /></button>
                    </div>
                  ))}
                </div>
                {post.files?.length > 1 && <p className="text-xs text-subtle -mt-1 mb-2">Arrastra las imágenes para reordenar el carrusel. El número indica el orden que verá el cliente.</p>}
                <FileUpload onUploaded={attach} />
              </div>

              {(post.channel === "instagram_reel" || post.type === "video") && (
                <div data-testid="cover-section">
                  <span className="text-sm text-subtle">Portada del reel</span>
                  <p className="text-xs text-subtle mb-2">La imagen que verá el cliente antes de reproducir el video.</p>
                  {post.cover_url && <img src={fileUrl(post.cover_url)} alt="" className="w-24 h-32 object-cover border border-line rounded-sm mb-2" />}
                  <FileUpload onUploaded={setCover} />
                  {post.files?.some((f) => f.type === "video") && (
                    <button onClick={captureFrame} data-testid="capture-cover" className="mt-2 w-full py-2 border border-line rounded-sm text-sm flex items-center justify-center gap-2"><Camera size={14} /> Usar un fotograma del video como portada</button>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <F label="Fecha" className="flex-1"><input type="date" min={todayStr()} value={post.date} onChange={(e) => setPost({ ...post, date: e.target.value })} className="inp font-data" /></F>
                <F label="Hora" className="w-28"><input type="time" value={post.time} onChange={(e) => setPost({ ...post, time: e.target.value })} className="inp font-data" /></F>
              </div>
              <div className="flex gap-3">
                <F label="Canal" className="flex-1"><select value={post.channel} onChange={(e) => { const ch = e.target.value; setPost({ ...post, channel: ch, type: ch === "instagram_reel" ? "video" : post.type }); }} data-testid="edit-channel" className="inp">{CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></F>
                {post.channel === "instagram_reel" ? (
                  <F label="Tipo" className="flex-1"><div className="inp bg-app text-subtle">Reel (video)</div></F>
                ) : (
                  <F label="Tipo" className="flex-1"><select value={post.type} onChange={(e) => setPost({ ...post, type: e.target.value })} className="inp">{POST_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select></F>
                )}
              </div>
              <F label="Estado"><select value={post.status} onChange={(e) => setStatus(e.target.value)} data-testid="status-select" className="inp">{STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select></F>

              <div>
                <F label="Texto de publicación"><textarea value={post.text} onChange={(e) => setPost({ ...post, text: e.target.value })} rows={4} data-testid="edit-text" className="inp resize-none" /></F>
                <div className="text-right font-data text-xs text-subtle mt-1" data-testid="char-count">{charCount} caracteres</div>
              </div>
              <F label="Hashtags"><input value={post.hashtags} onChange={(e) => setPost({ ...post, hashtags: e.target.value })} className="inp" placeholder="#marca #campaña" /></F>

              <div className="flex flex-wrap gap-2 pt-3 border-t border-line">
                <span data-testid="save-indicator" className="px-3 py-2.5 text-sm text-subtle flex items-center gap-1.5">{savedState === "saving" ? "Guardando…" : savedState === "saved" ? "Guardado ✓" : "Se guarda solo"}</span>
                <button onClick={send} data-testid="send-client" className="px-4 py-2.5 border border-line text-sm rounded-sm flex items-center gap-1.5"><Send size={14} /> Enviar al cliente</button>
                {post.status === "cambios_solicitados" && <button onClick={newVersion} data-testid="new-version" className="px-4 py-2.5 border border-line text-sm rounded-sm">Subir nueva versión</button>}
                <button onClick={archive} data-testid="archive-post" className="px-4 py-2.5 border border-line text-sm rounded-sm flex items-center gap-1.5"><Archive size={14} /> {post.archived ? "Restaurar" : "Archivar"}</button>
                <button onClick={() => setConfirmDel(true)} data-testid="delete-post" className="px-4 py-2.5 border border-line text-sm rounded-sm flex items-center gap-1.5 text-changes"><Trash2 size={14} /> Eliminar</button>
              </div>
            </div>
          ) : (
            <div className="h-[560px]">
              <Comments postId={postId} isVideo={isVideo} videoSecond={videoSecond} onSeek={seek} mode="studio" />
            </div>
          )}
        </div>
      </div>

      {showHistory && <HistoryModal postId={postId} onClose={() => setShowHistory(false)} />}
      {confirmDel && (
        <div className="fixed inset-0 bg-ink/30 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDel(false)}>
          <div className="bg-surface border border-line w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm">
            <h2 className="font-display text-2xl mb-2">Eliminar post</h2>
            <p className="text-subtle text-sm mb-5">Se borrará el post con sus archivos, comentarios e historial. Esta acción no se puede deshacer.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDel(false)} className="flex-1 py-2.5 border border-line text-sm rounded-sm">Cancelar</button>
              <button onClick={del} data-testid="confirm-delete" className="flex-1 py-2.5 text-white text-sm rounded-sm bg-changes">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </StudioLayout>
  );
}

function Tab({ active, onClick, children, testid }) {
  return <button onClick={onClick} data-testid={testid} className={`px-4 py-2.5 text-sm border-b-2 -mb-px ${active ? "border-ink text-ink" : "border-transparent text-subtle"}`}>{children}</button>;
}
function F({ label, children, className = "" }) {
  return <label className={`block ${className}`}><span className="text-sm text-subtle">{label}</span><div className="mt-1">{children}</div></label>;
}

function HistoryModal({ postId, onClose }) {
  const [versions, setVersions] = useState([]);
  useEffect(() => { api.get(`/posts/${postId}/versions`).then((r) => setVersions(r.data.reverse())); }, [postId]);
  return (
    <div className="fixed inset-0 bg-ink/30 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface border border-line w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="history-modal">
        <div className="flex items-center justify-between mb-5"><h2 className="font-display text-2xl">Historial de versiones</h2><button onClick={onClose}><X size={18} /></button></div>
        <div className="space-y-5">
          {versions.map((v) => (
            <div key={v.version} className="border-b border-line pb-4">
              <div className="font-data text-sm mb-2">v{v.version}</div>
              <div className="flex gap-2 mb-2">{v.files.map((f) => <img key={f.id} src={fileUrl(f.thumbnail || f.url)} alt="" className="w-14 h-14 object-cover border border-line" />)}</div>
              {v.approvals.map((a) => (
                <div key={a.id} className="text-xs text-subtle">
                  <span style={{ color: a.decision === "aprobado" ? "#3F6B4F" : "#A63D2E" }}>{a.decision === "aprobado" ? "Aprobado" : "Cambios solicitados"}</span> por {a.who} · {fmtTs(a.created_at)}{a.reason ? ` — ${a.reason}` : ""}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


